import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OrgRoleSlice, RoleState } from "./roles";
import { PermissionsState } from "./permissions";
import { Toastify } from "../../../components/Toaster";
import { InviteUsersState, OrganizationUsersState } from "./users";
import { errorSideEffects, loadingSideEffects, successSideEffects, updatingSideEffects } from "..";

interface AddOrganizationPayload {
  Name: string;
  Metadata: {
    [key: string]: string;
  };
  Domains: Domain[];
  IsAuthRestrictedToDomain: boolean;
}
interface UpdateOrganizationPayload {
  Id: string;
  Name: string;
  Metadata: {
    [key: string]: string;
  };
  Domains: Domain[];
  IsAuthRestrictedToDomain: boolean;
}
interface Domain {
  Id?: string;
  DomainName: string;
  IsVerified: boolean;
  VerificationToken?: string;
  VerificationStrategy?: string;
}

interface PasswordPolicy {
  MinLength: number;
  MaxLength: number;
  RequireUppercase: boolean;
  RequireLowercase: boolean;
  RequireNumber: boolean;
  RequireSpecialChar: boolean;
  ExpiryDays: number;
}

interface MFAPolicy {
  EnforcementMode: string;
}

interface SessionPolicy {
  AccessTokenTTL: number;
  RefreshTokenTTL: number;
}

interface MemberPolicy {
  DefaultMemberRole: string;
}

interface Policies {
  PasswordPolicy: PasswordPolicy;
  MFAPolicy: MFAPolicy;
  SessionPolicy: SessionPolicy;
  MemberPolicy: MemberPolicy;
  JITPolicy: JITPolicy;
}

interface JITPolicy {
  Enabled: boolean;
}

interface SPCertificate {
  Certificate: string;
}

interface IDPCertificate {
  Certificate: string;
  NotBefore: string;
  NotAfter: string;
}

export interface GroupRoleConnection {
  GroupId: string;
  RoleId: string;
  Name: string;
  Id?: string;
  connId?: string;
  orgId?: string;
}

export interface GroupRoleConnectionResponse {
  orgId: string;
  connId: string;
  groupRoles: GroupRoleConnection[];
}

export interface GroupRoleUpdateResponse {
  orgId: string;
  connId: string;
  groupRole: GroupRoleConnection;
}

export interface Connection {
  Id: string;
  IsActive: boolean;
  Name: string;
  ConnectionType: string;
  Domain: string;
  Attributes: {
    Id: string;
    Email: string;
    FirstName: string;
    LastName: string;
  };
  CreatedDate: string;
  SPCertificate?: SPCertificate;
  IdpMetadataUrl?: string;
  IdpEntityId?: string;
  IdpLoginUrl?: string;
  IdpLogoutUrl?: string;
  IdpCertificate?: IDPCertificate;
  Issuer?: string;
  AuthorizationUrl?: string;
  TokenUrl?: string;
  UserInfoUrl?: string;
  UserInfoExtractByIdToken?: boolean;
  JWKSEndpoint?: string;
  ClientId?: string;
  ClientSecret?: string;
  Scopes?: string[];
  GroupRoles: GroupRoleConnection[];
}

interface DeleteConnectionRequestModel {
  orgId: string;
  connId: string;
}

interface ConnectionRequestModel extends DeleteConnectionRequestModel {
  Active?: boolean;
}

interface ConnectionStatusUpdateResponse extends DeleteConnectionRequestModel {
  IsActive: boolean;
}

interface CreateDirectoryRequestModel {
  orgId: string;
  Name: string;
  IDPType: string;
}

interface DirectoryTokenSecret {
  tokenName: string;
  tokenValue: string;
  tokenId: string;
  endpoint?: string;
}

interface CreateDirectoryResponse {
  Directory?: Directory | null;
  Token?: {
    Id?: string;
    Name?: string;
    Token?: string;
    Value?: string;
    OpaqueToken?: string;
    OpaqueValue?: string;
  };
  Endpoint?: string;
  [key: string]: any;
}

interface DeleteDirectoryRequestModel {
  orgId: string;
}

interface DeleteDirectoryTokenRequestModel {
  orgId: string;
  tokenId: string;
}

interface CreateDirectoryTokenRequestModel {
  orgId: string;
  Name: string;
}

export interface DirectoryToken {
  Id: string;
  Name: string;
  CreatedDate: string;
  LastUsed: string;
  Status?: string;
  Token?: string;
  Value?: string;
  OpaqueToken?: string;
  OpaqueValue?: string;
}

export interface Directory {
  Id: string;
  Name: string;
  IDPType: string;
  Tokens: DirectoryToken[];
  CreatedDate: string;
  ModifiedDate: string;
  ScimEndpoint?: string;
  SCIMEndpoint?: string;
  Status?: string;
  SyncStatus?: string;
}

export interface Organization {
  Id: string;
  Name: string;
  Metadata?: {
    Issuer: string;
  };
  IsActive: boolean;
  Domains?: Domain[];
  IsAuthRestrictedToDomain: boolean;
  Policies?: Policies;
  Connections?: Connection[];
  Directory?: Directory | null;
  CreatedDate: string;
  ModifiedDate: string;
  CreatedBy: string;
}

interface OrganizationStatusResponse {
  Id: string;
  IsActive: boolean;
}

interface OrganizationsState {
  Data: Organization[];
  isLoading: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isAdding: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;
}

export interface OrganizationSchema {
  orgRoles: RoleState;
  tenantRoles: RoleState;
  permissions: PermissionsState;
  organization: OrganizationsState;
  orgUsers: OrganizationUsersState;
  orgUserInvitations: InviteUsersState;
  currentOrganization: CurrentOrganizationState;
}
interface CurrentOrganizationState {
  Data: Organization | null;
  isLoading: boolean;
  isUpdating: boolean;
  isSuccess: boolean;
  isError: boolean;
  errorMessage: string | null;

  // Connections
  isConnectionAdding: boolean;
  isConnectionUpdating: boolean;
  isConnectionDeleting: boolean;

  // GroupRoles
  isGroupRoleAdding: boolean;
  isGroupRoleUpdating: boolean;
  isGroupRoleDeleting: boolean;

  // Policies
  isPolicyUpdating: boolean;

  // Directory
  isDirectoryDeleting: boolean;
  isDirectoryCreating: boolean;
  newDirectoryToken: DirectoryTokenSecret | null;
  isDirectoryTokenDeleting: boolean;
  deletingTokenId: string | null;
  isDirectoryTokenCreating: boolean;
  newlyCreatedToken: DirectoryTokenSecret | null;
}

const initialState: OrganizationsState = {
  Data: [],
  isUpdating: false,
  isAdding: false,
  isDeleting: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};
interface OrganizationAPIResponse {
  ErrorCode?: string;
  Message?: string;
  Data: Organization;
}

interface GetAllOrganizationAPIResponse {
  ErrorCode: string;
  essage: string;
  Data: Organization[];
}

const currentOrganizationInitialState: CurrentOrganizationState = {
  Data: null,
  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null,

  //Connections
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  //GroupRoles
  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  //Policies

  isPolicyUpdating: false,

  //Directory
  isDirectoryDeleting: false,
  isDirectoryCreating: false,
  newDirectoryToken: null,
  isDirectoryTokenDeleting: false,
  deletingTokenId: null,
  isDirectoryTokenCreating: false,
  newlyCreatedToken: null
};

const connectionAddingSideEffects = {
  isConnectionAdding: true,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const connectionUpdatingSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: true,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const connectionDeletingSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: true,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const connectionSuccessSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const connectionErrorSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

const groupRoleAddingSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: true,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const groupRoleUpdatingSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: true,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const groupRoleDeletingSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: true,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const groupRoleSuccessSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const groupRoleErrorSideEffects = {
  isConnectionAdding: false,
  isConnectionUpdating: false,
  isConnectionDeleting: false,

  isGroupRoleAdding: false,
  isGroupRoleUpdating: false,
  isGroupRoleDeleting: false,

  isUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

const policyUpdatingSideEffects = {
  isPolicyUpdating: true,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};
const policySuccessSideEffects = {
  isPolicyUpdating: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const policyErrorSideEffects = {
  isPolicyUpdating: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

const directoryDeletingSideEffects = {
  isDirectoryDeleting: true,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const directorySuccessSideEffects = {
  isDirectoryDeleting: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const directoryErrorSideEffects = {
  isDirectoryDeleting: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

const directoryCreatingSideEffects = {
  isDirectoryCreating: true,
  isDirectoryDeleting: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const directoryCreateSuccessSideEffects = {
  isDirectoryCreating: false,
  isDirectoryDeleting: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const directoryCreateErrorSideEffects = {
  isDirectoryCreating: false,
  isDirectoryDeleting: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

const directoryTokenDeletingSideEffects = {
  isDirectoryTokenDeleting: true,
  deletingTokenId: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const directoryTokenDeleteSuccessSideEffects = {
  isDirectoryTokenDeleting: false,
  deletingTokenId: null,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const directoryTokenDeleteErrorSideEffects = {
  isDirectoryTokenDeleting: false,
  isLoading: false,
  isSuccess: false,
  isError: true,
  deletingTokenId: null
};

const directoryTokenCreatingSideEffects = {
  isDirectoryTokenCreating: true,
  isLoading: false,
  isSuccess: false,
  isError: false,
  errorMessage: null
};

const directoryTokenCreateSuccessSideEffects = {
  isDirectoryTokenCreating: false,
  isLoading: false,
  isSuccess: true,
  isError: false,
  errorMessage: null
};

const directoryTokenCreateErrorSideEffects = {
  isDirectoryTokenCreating: false,
  isLoading: false,
  isSuccess: false,
  isError: true
};

export const OrganizationSlice = createSlice({
  name: "organization",
  initialState,
  reducers: {
    getOrganizationRequest(state) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isUpdating = false;
      state.isAdding = false;
      state.isDeleting = false;
      state.isError = false;
      state.errorMessage = null;
    },
    getOrganizationSuccess(state, action: PayloadAction<GetAllOrganizationAPIResponse>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.Data = [...action.payload.Data].sort((a, b) => {
        return new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime();
      });
    },
    getOrganizationFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    addOrganizationRequest(state, _: PayloadAction<AddOrganizationPayload>) {
      state.isAdding = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    addOrganizationSuccess(state, action: PayloadAction<Organization>) {
      state.isAdding = false;
      state.isSuccess = true;
      state.Data.unshift(action.payload);
      Toastify("success", "Organization added successfully");
    },
    addOrganizationFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isAdding = false;

      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateOrganizationRequest(state, _: PayloadAction<UpdateOrganizationPayload>) {
      state.isUpdating = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    updateOrganizationSuccess(state, action: PayloadAction<OrganizationAPIResponse>) {
      state.isUpdating = false;
      state.isSuccess = true;
      const index = state.Data.findIndex((org) => org.Id === action.payload.Data.Id);
      if (index !== -1) {
        state.Data[index] = action.payload.Data;
        Toastify("success", "Organization updated successfully");
      } else {
        Toastify("error", "Organization not found");
      }
    },
    updateOrganizationFailure(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateOrganizationStatusRequest(state, _: PayloadAction<any>) {
      Object.assign(state, updatingSideEffects);
    },
    updateOrganizationStatusSuccess(state, action: PayloadAction<OrganizationStatusResponse>) {
      Object.assign(state, successSideEffects);
      const index = state.Data.findIndex((org) => org.Id === action.payload.Id);
      if (index !== -1) {
        state.Data[index].IsActive = action.payload.IsActive;
        Toastify("success", `Organization ${action.payload.IsActive ? "activated" : "deactivated"} successfully`);
      }
    },
    updateOrganizationStatusFailure(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    },
    deleteOrganizationRequest(state, _: PayloadAction<Organization>) {
      state.isDeleting = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    deleteOrganizationSuccess(state, action: PayloadAction<Organization>) {
      state.isDeleting = false;
      state.isSuccess = true;
      state.Data = state.Data.filter((org) => org.Id !== action.payload.Id);
      Toastify("success", "Organization deleted successfully");
    },
    deleteOrganizationFailure(state, action: PayloadAction<string>) {
      state.isDeleting = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  }
});

export const CurrentOrganizationSlice = createSlice({
  name: "currentOrganization",
  initialState: currentOrganizationInitialState,
  reducers: {
    getCurrentOrganizationRequest(state, _: PayloadAction<string>) {
      state.isLoading = true;
      state.isSuccess = false;
      state.isUpdating = false;
      state.isError = false;
      state.errorMessage = null;
    },
    getCurrentOrganizationSuccess(state, action: PayloadAction<Organization>) {
      state.isLoading = false;
      state.isSuccess = true;
      state.Data = action.payload;
      state.isDirectoryCreating = false;
      state.newDirectoryToken = null;
      state.isDirectoryTokenDeleting = false;
      state.deletingTokenId = null;
      state.isDirectoryTokenCreating = false;
      state.newlyCreatedToken = null;
    },
    getCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      state.isLoading = false;
      state.isError = true;
      state.errorMessage = action.payload;
    },
    updateCurrentOrganizationRequest(state) {
      state.isUpdating = true;
      state.isSuccess = false;
      state.isError = false;
      state.errorMessage = null;
    },
    updateCurrentOrganizationSuccess(state, action: PayloadAction<Organization>) {
      state.isUpdating = false;
      state.isSuccess = true;
      state.Data = action.payload;
    },
    updateCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      state.isUpdating = false;
      state.isError = true;
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    //Policies
    updateMFAPolicyForCurrentOrganizationRequest(state, _: PayloadAction<{ Id: string; EnforcementMode: string }>) {
      Object.assign(state, policyUpdatingSideEffects);
    },
    updateMFAPolicyForCurrentOrganizationSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, policySuccessSideEffects);
      if (state.Data) {
        state.Data.Policies.MFAPolicy.EnforcementMode = action.payload;
      }
      Toastify("success", "MFA Policy updated successfully");
    },
    updateMFAPolicyForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, policyErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateJITPolicyForCurrentOrganizationRequest(state, _: PayloadAction<{ Id: string; Status: boolean }>) {
      Object.assign(state, policyUpdatingSideEffects);
    },
    updateJITPolicyForCurrentOrganizationSuccess(state, action: PayloadAction<boolean>) {
      Object.assign(state, policySuccessSideEffects);
      if (state.Data) {
        state.Data.Policies.JITPolicy.Enabled = action.payload;
      }
      Toastify("success", "JIT Policy updated successfully");
    },
    updateJITPolicyForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, policyErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    //Connections
    createConnectionForCurrentOrganizationRequest(state, _: PayloadAction<any>) {
      Object.assign(state, connectionAddingSideEffects);
    },

    createConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<Connection>) {
      Object.assign(state, connectionSuccessSideEffects);
      if (state.Data) {
        state.Data.Connections = [...(state.Data.Connections || []), action.payload];
      }
      Toastify("success", "Connection created successfully");
    },
    createConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, connectionErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateConnectionForCurrentOrganizationRequest(state, _: PayloadAction<Connection>) {
      Object.assign(state, connectionUpdatingSideEffects);
    },
    updateConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<Connection>) {
      Object.assign(state, connectionSuccessSideEffects);
      if (state.Data) {
        const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.Id);
        if (index !== -1) {
          state.Data.Connections[index] = action.payload;
        }
      }
      Toastify("success", "Connection updated successfully");
    },
    updateConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, connectionErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    createDirectoryForCurrentOrganizationRequest(state, _: PayloadAction<CreateDirectoryRequestModel>) {
      Object.assign(state, directoryCreatingSideEffects);
      state.newDirectoryToken = null;
    },
    createDirectoryForCurrentOrganizationSuccess(state, action: PayloadAction<CreateDirectoryResponse>) {
      Object.assign(state, directoryCreateSuccessSideEffects);
      const directoryPayload = (action.payload?.Directory as Directory) || (action.payload as unknown as Directory);
      if (state.Data && directoryPayload) {
        state.Data.Directory = directoryPayload;
      }
      const tokensFromDirectory = directoryPayload?.Tokens || [];
      const tokenPayload = (action.payload?.Token as DirectoryToken) || tokensFromDirectory[0] || ({} as DirectoryToken);
      const tokenValue = tokenPayload.OpaqueToken || tokenPayload.Token || tokenPayload.Value || "";
      const tokenName = tokenPayload.Name || tokensFromDirectory[0]?.Name || "";
      const tokenId = tokenPayload.Id || tokensFromDirectory[0]?.Id || "";
      const endpoint = action.payload?.Endpoint || directoryPayload?.ScimEndpoint || directoryPayload?.SCIMEndpoint || "";
      state.newDirectoryToken = tokenValue
        ? {
            tokenName,
            tokenValue,
            tokenId,
            endpoint
          }
        : null;
      Toastify("success", "Directory created successfully");
    },
    createDirectoryForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, directoryCreateErrorSideEffects);
      state.errorMessage = action.payload;
      state.newDirectoryToken = null;
      Toastify("error", action.payload);
    },
    createDirectoryTokenForCurrentOrganizationRequest(state, _: PayloadAction<CreateDirectoryTokenRequestModel>) {
      Object.assign(state, directoryTokenCreatingSideEffects);
      state.newlyCreatedToken = null;
    },
    createDirectoryTokenForCurrentOrganizationSuccess(state, action: PayloadAction<DirectoryToken>) {
      Object.assign(state, directoryTokenCreateSuccessSideEffects);
      if (state.Data?.Directory) {
        const sanitizedToken: DirectoryToken = {
          Id: action.payload.Id,
          Name: action.payload.Name,
          CreatedDate: action.payload.CreatedDate || new Date().toISOString(),
          LastUsed: action.payload.LastUsed || "",
          Status: action.payload.Status,
          Token: undefined,
          Value: undefined
        } as DirectoryToken;
        state.Data.Directory.Tokens = [...(state.Data.Directory.Tokens || []), sanitizedToken];
      }
      const tokenValue = action.payload.OpaqueToken || action.payload.Token || action.payload.Value || "";
      state.newlyCreatedToken = tokenValue
        ? {
            tokenName: action.payload.Name,
            tokenValue,
            tokenId: action.payload.Id
          }
        : null;
      Toastify("success", "Token created successfully");
    },
    createDirectoryTokenForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, directoryTokenCreateErrorSideEffects);
      state.errorMessage = action.payload;
      state.newlyCreatedToken = null;
      Toastify("error", action.payload);
    },
    deleteConnectionForCurrentOrganizationRequest(state, _: PayloadAction<DeleteConnectionRequestModel>) {
      Object.assign(state, connectionDeletingSideEffects);
    },
    deleteConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, connectionSuccessSideEffects);
      if (state.Data) {
        state.Data.Connections = state.Data.Connections.filter((conn) => conn.Id !== action.payload);
        state.Data.Policies.JITPolicy.Enabled = false;
      }
      Toastify("success", "Connection deleted successfully");
    },
    deleteConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, connectionErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteDirectoryForCurrentOrganizationRequest(state, _: PayloadAction<DeleteDirectoryRequestModel>) {
      Object.assign(state, directoryDeletingSideEffects);
    },
    deleteDirectoryForCurrentOrganizationSuccess(state) {
      Object.assign(state, directorySuccessSideEffects);
      if (state.Data) {
        state.Data.Directory = null;
      }
      state.newDirectoryToken = null;
      Toastify("success", "Directory deleted successfully");
    },
    deleteDirectoryForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, directoryErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteDirectoryTokenForCurrentOrganizationRequest(state, action: PayloadAction<DeleteDirectoryTokenRequestModel>) {
      Object.assign(state, directoryTokenDeletingSideEffects);
      state.deletingTokenId = action.payload.tokenId;
    },
    deleteDirectoryTokenForCurrentOrganizationSuccess(state, action: PayloadAction<string>) {
      Object.assign(state, directoryTokenDeleteSuccessSideEffects);
      if (state.Data?.Directory?.Tokens) {
        state.Data.Directory.Tokens = state.Data.Directory.Tokens.filter((token) => token.Id !== action.payload);
      }
      Toastify("success", "Token deleted successfully");
    },
    deleteDirectoryTokenForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, directoryTokenDeleteErrorSideEffects);
      state.errorMessage = action.payload;
      state.deletingTokenId = null;
      Toastify("error", action.payload);
    },
    clearNewDirectoryToken(state) {
      state.newDirectoryToken = null;
      state.isDirectoryCreating = false;
    },
    clearNewlyCreatedToken(state) {
      state.newlyCreatedToken = null;
      state.isDirectoryTokenCreating = false;
    },
    updateConnectionStatusForCurrentOrganizationRequest(state, _: PayloadAction<ConnectionRequestModel>) {
      Object.assign(state, connectionUpdatingSideEffects);
    },
    updateConnectionStatusForCurrentOrganizationSuccess(state, action: PayloadAction<ConnectionStatusUpdateResponse>) {
      Object.assign(state, connectionSuccessSideEffects);
      if (state.Data) {
        const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.connId);
        if (index !== -1) {
          state.Data.Connections[index].IsActive = action.payload.IsActive;
          !action.payload.IsActive && (state.Data.Policies.JITPolicy.Enabled = false);
        }
      }
      Toastify("success", "Connection status updated successfully");
    },
    updateConnectionStatusForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, connectionErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },

    //Groups
    getAllGroupRoleConnectionForCurrentOrganizationRequest(state, _: PayloadAction<DeleteConnectionRequestModel>) {
      Object.assign(state, loadingSideEffects);
    },
    getAllGroupRoleConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<GroupRoleConnectionResponse>) {
      Object.assign(state, groupRoleSuccessSideEffects);
      const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.connId);
      if (index !== -1) {
        state.Data.Connections[index].GroupRoles = action.payload.groupRoles;
      }
    },
    getAllGroupRoleConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, groupRoleErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    createGroupRoleConnectionForCurrentOrganizationRequest(state, _: PayloadAction<GroupRoleUpdateResponse>) {
      Object.assign(state, groupRoleAddingSideEffects);
    },
    createGroupRoleConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<GroupRoleUpdateResponse>) {
      Object.assign(state, groupRoleSuccessSideEffects);
      const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.connId);
      if (index !== -1) {
        state.Data.Connections[index].GroupRoles = state.Data.Connections[index].GroupRoles || [];
        state.Data.Connections[index].GroupRoles.push(action.payload.groupRole as GroupRoleConnection);
      }
      Toastify("success", "Group created successfully");
    },
    createGroupRoleConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, groupRoleErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    updateGroupRoleConnectionForCurrentOrganizationRequest(state, _: PayloadAction<GroupRoleUpdateResponse>) {
      Object.assign(state, groupRoleUpdatingSideEffects);
    },
    updateGroupRoleConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<GroupRoleUpdateResponse>) {
      Object.assign(state, groupRoleSuccessSideEffects);
      const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.connId);
      if (index !== -1) {
        const groupIndex = state.Data.Connections[index].GroupRoles.findIndex((group) => group.Id === action.payload.groupRole.Id);
        if (groupIndex !== -1) {
          state.Data.Connections[index].GroupRoles[groupIndex] = action.payload.groupRole as GroupRoleConnection;
        }
      }
      Toastify("success", "Group updated successfully");
    },
    updateGroupRoleConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, groupRoleErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    },
    deleteGroupRoleConnectionForCurrentOrganizationRequest(state, _: PayloadAction<any>) {
      Object.assign(state, groupRoleDeletingSideEffects);
    },
    deleteGroupRoleConnectionForCurrentOrganizationSuccess(state, action: PayloadAction<{ connId: string; groupRoleId: string; orgId: string }>) {
      Object.assign(state, groupRoleSuccessSideEffects);
      const index = state.Data.Connections.findIndex((conn) => conn.Id === action.payload.connId);
      if (index !== -1) {
        state.Data.Connections[index].GroupRoles = state.Data.Connections[index].GroupRoles.filter((group) => group.Id !== action.payload.groupRoleId);
      }
      Toastify("success", "Group deleted successfully");
    },
    deleteGroupRoleConnectionForCurrentOrganizationFailure(state, action: PayloadAction<string>) {
      Object.assign(state, groupRoleErrorSideEffects);
      state.errorMessage = action.payload;
      Toastify("error", action.payload);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(OrgRoleSlice.actions.makeRoleDefaultSuccess, (state, action) => {
      Object.assign(state, successSideEffects);
      state.Data.Policies.MemberPolicy = state.Data.Policies.MemberPolicy || { DefaultMemberRole: "" };
      state.Data.Policies.MemberPolicy.DefaultMemberRole = action.payload;
    });

    builder.addCase(OrganizationSlice.actions.updateOrganizationStatusSuccess, (state, action) => {
      Object.assign(state, successSideEffects);
      if (state.Data && state.Data.Id === action.payload.Id && (state.Data.IsActive = action.payload.IsActive)) {
        state.Data.IsActive = action.payload.IsActive;
      }
    });
    builder.addCase(OrganizationSlice.actions.updateOrganizationStatusFailure, (state, action) => {
      Object.assign(state, errorSideEffects);
      state.errorMessage = action.payload;
    });
    builder.addCase(OrganizationSlice.actions.updateOrganizationStatusRequest, (state, _) => {
      Object.assign(state, updatingSideEffects);
    });
  }
});

export const {
  getOrganizationRequest,
  getOrganizationSuccess,
  getOrganizationFailure,
  addOrganizationRequest,
  addOrganizationSuccess,
  addOrganizationFailure,
  updateOrganizationRequest,
  updateOrganizationSuccess,
  updateOrganizationFailure,
  updateOrganizationStatusRequest,
  updateOrganizationStatusSuccess,
  updateOrganizationStatusFailure,
  deleteOrganizationRequest,
  deleteOrganizationSuccess,
  deleteOrganizationFailure
} = OrganizationSlice.actions;

export const {
  getCurrentOrganizationRequest,
  getCurrentOrganizationSuccess,
  getCurrentOrganizationFailure,
  updateCurrentOrganizationRequest,
  updateCurrentOrganizationSuccess,
  updateCurrentOrganizationFailure,
  updateJITPolicyForCurrentOrganizationRequest,
  updateJITPolicyForCurrentOrganizationSuccess,
  updateJITPolicyForCurrentOrganizationFailure,
  updateMFAPolicyForCurrentOrganizationRequest,
  updateMFAPolicyForCurrentOrganizationSuccess,
  updateMFAPolicyForCurrentOrganizationFailure,
  createConnectionForCurrentOrganizationRequest,
  createConnectionForCurrentOrganizationSuccess,
  createConnectionForCurrentOrganizationFailure,
  updateConnectionForCurrentOrganizationRequest,
  updateConnectionForCurrentOrganizationSuccess,
  updateConnectionForCurrentOrganizationFailure,
  createDirectoryForCurrentOrganizationRequest,
  createDirectoryForCurrentOrganizationSuccess,
  createDirectoryForCurrentOrganizationFailure,
  createDirectoryTokenForCurrentOrganizationRequest,
  createDirectoryTokenForCurrentOrganizationSuccess,
  createDirectoryTokenForCurrentOrganizationFailure,
  deleteConnectionForCurrentOrganizationRequest,
  deleteConnectionForCurrentOrganizationSuccess,
  deleteConnectionForCurrentOrganizationFailure,
  deleteDirectoryForCurrentOrganizationRequest,
  deleteDirectoryForCurrentOrganizationSuccess,
  deleteDirectoryForCurrentOrganizationFailure,
  deleteDirectoryTokenForCurrentOrganizationRequest,
  deleteDirectoryTokenForCurrentOrganizationSuccess,
  deleteDirectoryTokenForCurrentOrganizationFailure,
  clearNewDirectoryToken,
  clearNewlyCreatedToken,
  updateConnectionStatusForCurrentOrganizationRequest,
  updateConnectionStatusForCurrentOrganizationSuccess,
  updateConnectionStatusForCurrentOrganizationFailure,
  createGroupRoleConnectionForCurrentOrganizationRequest,
  createGroupRoleConnectionForCurrentOrganizationSuccess,
  createGroupRoleConnectionForCurrentOrganizationFailure,
  deleteGroupRoleConnectionForCurrentOrganizationRequest,
  deleteGroupRoleConnectionForCurrentOrganizationSuccess,
  deleteGroupRoleConnectionForCurrentOrganizationFailure,
  updateGroupRoleConnectionForCurrentOrganizationRequest,
  updateGroupRoleConnectionForCurrentOrganizationSuccess,
  updateGroupRoleConnectionForCurrentOrganizationFailure,
  getAllGroupRoleConnectionForCurrentOrganizationRequest,
  getAllGroupRoleConnectionForCurrentOrganizationSuccess,
  getAllGroupRoleConnectionForCurrentOrganizationFailure
} = CurrentOrganizationSlice.actions;
